import { useCallback, useMemo } from 'react';
import { isNil } from 'shared/src/utils';
import { useGoodsDetail } from '../../../queries/useGoodsDetail';
import { useGoodsBasic } from '../../../queries/useGoodsBasic';
import type { GoodsDiscountGroupItem } from 'shared/src/api';
import { useGoodsDiscounts } from '../../../queries/useGoodsDiscounts';
import { useApplyDiscounts } from '../queries/useApplyDiscounts';

const ORDER_BENEFIT = {
  DEFAULT: 1,
  FIRST_ORDER: 2,
};

const UNIT_TYPE = {
  AMOUNT: 1,
  RATE: 2,
};

export const useAblyPayBenefitDiscount = (
  salePrice: number,
  checked: GoodsDiscountGroupItem[],
) => {
  const { data: discounts } = useGoodsDiscounts();
  const { data: applyDiscounts } = useApplyDiscounts({
    checked,
    groups: discounts?.discount_groups ?? [],
  });

  const { data: goodsDetail } = useGoodsDetail();
  const { data: goodsBasic } = useGoodsBasic();

  const ablyPayFirstOrderBenefitItem = useMemo(() => {
    if (isNil(goodsDetail)) return null;

    const firstOrderBenefitList =
      goodsDetail.ably_pay?.benefits.filter(
        benefit => benefit.benefit_type === ORDER_BENEFIT.FIRST_ORDER,
      ) ?? [];
    if (firstOrderBenefitList.length === 0) return null;

    return firstOrderBenefitList[0];
  }, [goodsDetail]);
  const ablyPayDefaultBenefitItem = useMemo(() => {
    if (isNil(goodsDetail)) return null;

    const defaultBenefitList =
      goodsDetail.ably_pay?.benefits.filter(
        benefit => benefit.benefit_type === ORDER_BENEFIT.DEFAULT,
      ) ?? [];
    if (defaultBenefitList.length === 0) return null;

    return defaultBenefitList[0];
  }, [goodsDetail]);

  const getBenefitItemPrice = useCallback<
    (salePrice: number, item: { unit_type: number; value: number }) => number
  >((salePrice, { unit_type, value }) => {
    if (unit_type === UNIT_TYPE.AMOUNT) {
      return value;
    }

    if (unit_type === UNIT_TYPE.RATE) {
      return (value * salePrice) / 100;
    }
    return 0;
  }, []);

  const discountAmount = useMemo(() => {
    if (isNil(ablyPayFirstOrderBenefitItem)) return null;
    if (isNil(ablyPayDefaultBenefitItem)) return null;
    if (isNil(goodsBasic)) return null;

    if (ablyPayFirstOrderBenefitItem.details.length === 0) return null;
    if (ablyPayDefaultBenefitItem.details.length === 0) return null;

    const goodsPrice = applyDiscounts?.sale_price ?? salePrice;

    const sortedFirstOrderBenefitList = ablyPayFirstOrderBenefitItem.details
      .filter(benefit => goodsPrice >= benefit.min_order_amount)
      .sort((a, b) => b.min_order_amount - a.min_order_amount);

    const sortedDefaultBenefitList = ablyPayDefaultBenefitItem.details
      .filter(benefit => goodsPrice >= benefit.min_order_amount)
      .sort((a, b) => b.min_order_amount - a.min_order_amount);

    if (sortedFirstOrderBenefitList.length === 0) return null;
    if (sortedDefaultBenefitList.length === 0) return null;

    const firstOrderBenefitItem = sortedFirstOrderBenefitList[0];
    const defaultBenefitItem = sortedDefaultBenefitList[0];

    return Math.floor(
      getBenefitItemPrice(goodsPrice, firstOrderBenefitItem) +
        getBenefitItemPrice(goodsPrice, defaultBenefitItem),
    );
  }, [
    ablyPayDefaultBenefitItem,
    ablyPayFirstOrderBenefitItem,
    applyDiscounts?.sale_price,
    getBenefitItemPrice,
    goodsBasic,
    salePrice,
  ]);

  return {
    ablyPayFirstOrderBenefitItem,
    discountAmount,
  };
};
